import React, { useRef, useState, useCallback, useEffect } from "react";
import icon2 from "../../assets/icons/addwhite.svg";
import icon3 from "../../assets/icons/download3.svg";
import useAuth from "../../hooks/useAuth";
import { useCart } from "../../context/CartContext";
import { useDownloadQueue } from "../../context/DownloadQueueContext";
import { useTheme } from "../../context/ThemeProvider";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import SubscribeModal from "../SubscribeModal";
import { hasActiveSubscription } from "../../utils/subscriptionUtils";
import { BsCartPlus } from "react-icons/bs";
import { toast } from "react-toastify";
import CartButton from "../Cart/CartButton";
import FavoriteButton from "./FavoriteButton";

const Video = ({ video, priority = false }) => {
  const [videoSrc, setVideoSrc] = useState(null); // State to load video src dynamically
  const [hasPlayed, setHasPlayed] = useState(false); // Track if video has played once
  const [thumbnailFailed, setThumbnailFailed] = useState(false);

  // console.log('Video component props:', video); // Check what reaches the component

  const navigate = useNavigate();
  const location = useLocation();
  const videoRef = useRef(null);
  const hoverTimeoutRef = useRef(null);
  const loadedataListenerRef = useRef(null);

  const handleMouseEnter = useCallback(() => {
    // Clear any pending timeout
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }

    if (!videoRef.current) return;

    const videoEl = videoRef.current;

    // STOP all other previews except this one (only for grid videos, not banner)
    document.querySelectorAll(".grid-video-item video[data-hover-preview='true']").forEach((vid) => {
      if (vid !== videoEl) {
        vid.pause();
        vid.style.opacity = 0;
      }
    });

    // If already played once, just show and play
    if (hasPlayed) {
      videoEl.style.opacity = 1;
      videoEl.play().catch((err) => console.warn("Play error:", err));
      return;
    }

    // Load src if needed
    if (!videoSrc) {
      setVideoSrc(video?.previewURL);
    } else {
      // Src is already loaded, play immediately
      attemptPlay(videoEl);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoSrc, hasPlayed, video?.previewURL]);

  const attemptPlay = useCallback((videoEl) => {
    const playVideo = () => {
      videoEl.style.opacity = 1;
      const playPromise = videoEl.play();
      if (playPromise !== undefined) {
        playPromise
          .then(() => {
            setHasPlayed(true);
          })
          .catch((err) => console.warn("Play error:", err));
      }
    };

    if (videoEl.readyState >= 2) {
      playVideo();
    } else {
      // Clean up old listener if it exists
      if (loadedataListenerRef.current) {
        videoEl.removeEventListener("loadeddata", loadedataListenerRef.current);
      }

      // Create new listener
      const handleLoadedData = () => {
        playVideo();
        videoEl.removeEventListener("loadeddata", handleLoadedData);
        loadedataListenerRef.current = null;
      };

      loadedataListenerRef.current = handleLoadedData;
      videoEl.addEventListener("loadeddata", handleLoadedData);

      // Set a timeout to remove listener if video doesn't load
      const timeoutId = setTimeout(() => {
        if (loadedataListenerRef.current) {
          videoEl.removeEventListener("loadeddata", loadedataListenerRef.current);
          loadedataListenerRef.current = null;
        }
      }, 5000);

      // Store timeout id to clear it on success
      videoEl.dataset.loadTimeout = timeoutId;
    }
  }, []);

  // Handle video src change - attempt to play once src is set
  useEffect(() => {
    if (videoSrc && videoRef.current) {
      attemptPlay(videoRef.current);
    }
  }, [videoSrc, attemptPlay]);

  const handleMouseLeave = useCallback(() => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }

    hoverTimeoutRef.current = setTimeout(() => {
      if (videoRef.current) {
        videoRef.current.pause();
        videoRef.current.style.opacity = 0;
      }
    }, 200);
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    const videoEl = videoRef.current;
    return () => {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
      if (loadedataListenerRef.current && videoEl) {
        videoEl.removeEventListener(
          "loadeddata",
          loadedataListenerRef.current
        );
      }
    };
  }, []);

  const { auth } = useAuth();
  const { darkMode } = useTheme();
  const { addToCart } = useCart();
  const isSubscribed = hasActiveSubscription(auth?.subscription);
  const [activeModal, setActiveModal] = useState(null); // null | 'similar' | 'add' | 'download'
  const [showSubscribeModal, setShowSubscribeModal] = useState(false);
  const [activeTooltip, setActiveTooltip] = useState(null);
  const { enqueueDownload, isDownloadActive } = useDownloadQueue();
  const thumbnailSrc =
    video?.thumnail || video?.thumbnail || video?.thumbnailUrl || "";
  const previewSrc = video?.previewURL || "";
  const showThumbnailImage = Boolean(thumbnailSrc) && !thumbnailFailed;

  const startBestDownload = () => {
    if (!auth?.RawToken || !video?._id) {
      toast.error("Please sign in again to download.");
      return;
    }

    const result = enqueueDownload({
      videoId: video._id,
      title: video.name,
      thumbnail: thumbnailSrc,
    });
    if (result.reason === "duplicate") {
      toast.info("This video is already in your downloads.", { autoClose: 2000 });
    }
  };

  const handleDownloadClick = async () => {
    if (!auth?.role) {
      setShowSubscribeModal(true);
      return;
    }

    if (hasActiveSubscription(auth.subscription)) {
      startBestDownload();
      return;
    }

    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/user/subscription`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${auth.RawToken}`,
          },
        },
      );

      if (response.data?.subscription?.isActive) {
        startBestDownload();
      } else {
        setShowSubscribeModal(true);
      }
    } catch (error) {
      console.error("Error checking subscription:", error);
      toast.error("Could not verify your subscription. Please try again.");
    }
  };

  const handleIconClick = (type) => {
    console.log(video);

    console.log(type, auth);

    if (!type) {
      console.error("Received undefined type", type);
      return;
    } else if (type === "addtocart") {
      (async () => {
        const result = await addToCart(video);
        if (result.success) {
          toast.success(result.message || "Added to cart");
        } else {
          toast.error(result.message || "Could not add to cart");
        }
      })();
      return;
    } else if (type === "add") {
      console.log(auth);

      if (!auth.role) {
        console.log("no auth found");
        // Open SubscribeModal instead of navigating to /subscribe
        setShowSubscribeModal(true);
      } else {
        setActiveModal("add");
      }
    } else if (type === "download") {
      handleDownloadClick();
    } else {
      if (type === "similar") {
        const collectionTerm = video?.collectionName?.trim();
        const themeTerm =
          (Array.isArray(video?.themes) && video.themes[0]) ||
          (Array.isArray(video?.theme) && video.theme[0]);
        const fallbackTerm = (video?.tags && video.tags[0]) || "";
        const searchTerm = collectionTerm || themeTerm || fallbackTerm;

        if (!searchTerm) {
          return;
        }

        const params = new URLSearchParams(location.search);
        const currentTerm = params.get("term") || "";
        const baseUrl = `/search?term=${encodeURIComponent(searchTerm)}`;

        if (location.pathname === "/search" && currentTerm === searchTerm) {
          navigate(`${baseUrl}&refresh=${Date.now()}`);
        } else {
          navigate(baseUrl);
        }
      }
      // setActiveModal(type);
    }
  };

  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(false);
  const [message, setMessage] = useState("");

  const [selectedQuality, setSelectedQuality] = useState("");

  const closeModal = useCallback(() => {
    setActiveModal(null);
    setLoading(false);
    setSuccess(false);
    setError(false);
    setMessage("");
  }, []);

  const SAVE_SUCCESS_MODAL_MS = 1500;
  useEffect(() => {
    if (activeModal !== "add" || !success) return;
    const id = window.setTimeout(() => {
      closeModal();
    }, SAVE_SUCCESS_MODAL_MS);
    return () => window.clearTimeout(id);
  }, [activeModal, success, closeModal]);

  const saveVideo = async () => {
    const token = auth.RawToken;
    console.log(auth)
    setLoading(true);
    setSuccess(false);
    setError(false);

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/user/save`,
        { id: video._id },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );
      console.log(response);
      if (response.data.success === true) {
        setSuccess(true);
        setLoading(false);
        setMessage(response.data.message);
      }
    } catch (e) {
      console.log(e);
      setLoading(false);
      setError(true);

      if (e.response?.status === 403) {
        console.log("need refresh");
        // window.location.reload();
      }
    }

    console.log(video);
  };

  const downloadMobileHD = async () => {
    try {
      if (!video?._id) {
        console.error("Invalid video");
        return;
      }
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/user/downloadmobile`,
        { id: video._id },
        {
          headers: {
            Authorization: `Bearer ${auth.RawToken}`,
          },
          responseType: "blob", // Important for file downloads
        }
      );

      // Create download link from the blob response
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `${video._id}.mp4`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
    } catch (e) {
      console.error("Download failed:", e);
      // Handle errors (show toast/notification)
    }
  };

  const download4kHD = async () => {
    console.log(selectedQuality)
    try {
      if (!video?._id) {
        console.error("Invalid video");
        return;
      }
      if (selectedQuality !== "HD" && selectedQuality !== "4K") {
        console.error("Invalid video quality");
        return;
      }
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/user/downloadhighres`,
        { id: video._id,quality:selectedQuality },
        {
          headers: {
            Authorization: `Bearer ${auth.RawToken}`,
          },
          responseType: "blob", // Important for file downloads
        }
      );

      // Create download link from the blob response
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `${video._id}.mp4`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
    } catch (e) {
      console.error("Download failed:", e);
      // Handle errors (show toast/notification)
    }
  };

  const renderModalContent = () => {
    // if (!auth || Object.keys(auth).length === 0) {
    //   return (
    //     <>
    //       <p className="text-sm">Please login to perform this action.</p>
    //       <button className="greenButton">Subscribe Now</button>
    //     </>
    //   );
    // }

    switch (activeModal) {
      case "similar":
        return (
          <>
            <h2 className="text-lg font-semibold mb-4 capitalize">
              Show Similar Videos
            </h2>
            <button className="greenButton">Show</button>
          </>
        );

      // Download modal intentionally kept for reference, but the active flow
      // now downloads the highest available quality immediately on icon click.
      // case "download":
      //   return (
      //     <>
      //       <h2 className="text-lg font-semibold mb-4 capitalize">Download</h2>
      //       <p className="text-sm">Click below to download the video:</p>
      //       {video.shotonmobile ? (
      //         <div className="flex items-center gap-2">
      //           <button onClick={downloadMobileHD} className="greenButton">
      //             Download Mobile HD
      //           </button>
      //         </div>
      //       ) : (
      //         <div className="w-full">
      //           <div className="whitespace-nowrap text-sm flex justify-center items-center gap-4">
      //             <div>Select Video Quality:</div>
      //             <select
      //               value={selectedQuality}
      //               onChange={(e) => setSelectedQuality(e.target.value)}
      //               className={`input min-w-fit border-b ${
      //                 darkMode && "dark !border-[#333333]"
      //               } text-sm`}
      //             >
      //               <option value="" disabled hidden>
      //                 Select quality
      //               </option>
      //               <option value="HD">HD</option>
      //               <option value="4K">4K</option>
      //             </select>
      //           </div>
      //           <button onClick={download4kHD} className="greenButton mt-6">
      //             Download
      //           </button>
      //         </div>
      //       )}
      //     </>
      //   );
      default:
        return null;
    }
  };

  return (
    <>


    <div
      className="relative group cursor-pointer h-full grid-video-item"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onClick={() =>
        navigate(`/video/${video.slug || video._id}`, {
          state: {
            backgroundLocation: location,
            from: `${location.pathname}${location.search}`,
            scrollY: window.scrollY || window.pageYOffset || 0,
          },
        })
      }
    >
      {/* Thumbnail */}
      {showThumbnailImage ? (
        <img
          src={thumbnailSrc}
          alt={`${video?.name || "Indian stock video"} stock video thumbnail`}
          width="640"
          height="360"
          className="w-full h-full object-cover"
          loading={priority ? "eager" : "lazy"}
          fetchPriority={priority ? "high" : "auto"}
          onError={() => setThumbnailFailed(true)}
        />
      ) : previewSrc ? (
        <video
          src={previewSrc}
          muted
          playsInline
          preload="metadata"
          className="w-full h-full object-cover"
          aria-label={`${video?.name || "Indian stock video"} preview`}
        />
      ) : (
        <div className="flex h-full w-full items-center justify-center bg-[#151815] px-4 text-center text-sm text-[var(--grey)]">
          Preview unavailable
        </div>
      )}

      {/* Video */}
      <video
        ref={videoRef}
        data-hover-preview="true"
        src={videoSrc} // Dynamically loaded src
        muted
        loop
        playsInline
        webkit-playsinline="true"
        x-webkit-airplay="deny"
        disablePictureInPicture
        className="pointer-events-none md:pointer-events-auto w-full h-full object-cover absolute top-0 left-0 opacity-0 transition-opacity duration-300"
        style={{ zIndex: 1 }}
      />

      {/* Hover Content */}
      <div 
    className="absolute inset-1 md:inset-3 flex flex-col justify-between gap-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
    style={{ zIndex: 2 }} // Make sure this is higher than video's z-index
  >
    <div className="flex flex-col justify-self-start gap-1 cursor-pointer">
      {/* Add any text here */}
    </div>

    <div className="flex gap-4 items-center justify-start relative">
      {!isSubscribed && <CartButton video={video} />}
      <FavoriteButton videoId={video._id} setShowSubscribeModal={setShowSubscribeModal} />
      {[
        { icon: icon3, label: "Download", type: "download" },
      ].map(({ icon, label, type }) => (
        <div
          key={type}
          className="relative flex items-center cursor-pointer"
          onMouseEnter={() => setActiveTooltip(type)}
          onMouseLeave={() => setActiveTooltip(null)}
          onFocus={() => setActiveTooltip(type)}
          onBlur={() => setActiveTooltip(null)}
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            handleIconClick(type);
          }}
        >
          <img
            src={icon}
            alt={label}
            width="32"
            height="32"
            className="h-8 object-contain"
          />
          <span
            className={`pointer-events-none absolute left-full top-1/2 ml-2 -translate-y-1/2 whitespace-nowrap text-[11px] ${
              darkMode
                ? "bg-[#1B1D1C] text-white"
                : "bg-white text-gray-700"
            } ${
              activeTooltip === type ? "opacity-100" : "opacity-0"
            } z-20 rounded px-2 py-1 shadow transition-opacity duration-150`}
          >
            {type === "download" && isDownloadActive(video?._id) ? "In downloads" : label}
          </span>
        </div>
      ))}
    </div>
  </div>
    </div>

      {/* Modal */}
      {activeModal && (
        <div
          className="fixed inset-0 bg-black bg-opacity-40 z-40 flex items-center justify-center"
          onClick={closeModal}
        >
          <div
            className={`${
              darkMode
                ? "bg-[#1B1D1C] border-[#333333]"
                : "bg-[#F1F1F1] border-[#CBCBCB]"
            } border rounded-xl p-6 w-[90%] max-w-sm z-50 shadow-lg flex flex-col items-center gap-6 text-center`}
            onClick={(e) => e.stopPropagation()}
          >
            {renderModalContent()}
          </div>
        </div>
      )}

      {showSubscribeModal && (
        <SubscribeModal video={video} setShowModal={setShowSubscribeModal} />
      )}
    </>
  );
};

export default Video;
